// Owner Recapitalization Loan — the seller funnel's recap arm and its path step
// (spec pack docs/owner-recap/approved/funnel_staff.json, surfaces F0–F3d).
//
// 🔴 PHOENIX-LOCKED (owner ruling OP7): every string here is a code constant,
// deliberately NOT a Document Language block. No partner brand and no admin
// edit can reach it — the note-not-cash and combined-cost statements are
// disclosures. Verbatim from the approved spec's `proposed` text; `{{…}}`
// values there are samples and are computed here.

import type { ChoiceOption } from "./prequalContent";
import type { KnockoutQuestionDef } from "./prequalContent";
import { SELLER_KNOCKOUTS } from "./prequalContent";
import type { PrequalSellerPath } from "../../api/prequalification";

// ── F0 — landing, third role card ──────────────────────────────────────────

export const FORK_TITLE_RECAP = "I’m staying in my business";
export const FORK_BODY_RECAP =
  "Keep running your business and convert part of its value into a note paid over time — an Owner Recapitalization Loan.";

/** The query value that preselects the recap path on `/prequalify[/:slug]/seller`
 *  (F0 "Direct link"). Any other value is ignored. */
export const RECAP_PATH_QUERY_VALUE = "recap";

/** Reads `?path=` off a seller-funnel URL: the recap path for `recap`, null for
 *  anything else (an unknown value is ignored and the Path step opens with
 *  nothing selected). */
export function sellerPathFromQuery(raw: string | null | undefined): PrequalSellerPath | null {
  return raw?.trim().toLowerCase() === RECAP_PATH_QUERY_VALUE ? "OwnerRecapitalization" : null;
}

// ── F1 — Step 1 · Path ─────────────────────────────────────────────────────

export const PATH_STEP_TITLE = "What are you planning for the business?";
export const PATH_STEP_HINT = "Choose one. It sets the questions and documents that follow.";

export interface SellerPathCard {
  value: PrequalSellerPath;
  label: string;
  body: string;
  produces: string;
}

/** Card A then Card B, in that order. */
export const SELLER_PATH_CARDS: readonly SellerPathCard[] = [
  {
    value: "Sale",
    label: "Sell the business",
    body: "You plan to sell to a new owner, who pays over time through a Phoenix-administered seller note.",
    produces: "You receive: prequalification letter · deal sheet · readiness analysis where offered",
  },
  {
    value: "OwnerRecapitalization",
    label: "Stay and recapitalize — Owner Recapitalization Loan",
    body: "You and any co-owners stay in the business and take value out of it. You form a new company that buys the business assets from your existing company, and your existing company holds the note.",
    produces: "You receive: recapitalization prequalification letter · deal sheet showing both companies' costs",
  },
];

export const PATH_RECAP_NOTICE =
  "Your existing company receives a note, not cash. Cash beyond the note's payments comes only from a sale of vested Pool Units, and no sale is assured.";
export const PATH_SWITCH_NOTICE = "Switching clears the answers that apply only to the other path.";
export const PATH_REQUIRED_ERROR = "Choose one to continue.";

// ── F2a — frame and rails on the recap arm ─────────────────────────────────

export const RECAP_PROGRAM_OVERLINE = "Phoenix Owner Recapitalization Loan";
export const RECAP_FUNNEL_TITLE = "Owner recapitalization prequalification";
export const RECAP_PROGRAM_TERMS_SUBHEAD =
  "10% fixed — the Current Program Rate, subject to change and to the Note Holder's final determination — on a 10- or 20-year amortization your new company elects, with a balloon at the end of Year 8. Financing is generally up to five times free cash flow on a 20-year schedule, and less on a 10-year schedule: the lower of 5× free cash flow and what the business can carry, with a margin, on the schedule elected. The program finances $250,000 to $10 million. In an owner recapitalization the purchase price equals the amount financed, and your existing company takes the full price as a note. Indicative only — the Note Holder decides.";

export const RECAP_METER_PENDING_NOTE =
  "Not yet — this appears once you enter the business's annual free cash flow.";
export const RECAP_METER_SCHEDULE_NOTE =
  "Also the purchase price in an owner recapitalization. Up to five times free cash flow on a 20-year schedule, and less on a 10-year schedule.";

/** Program at a glance — the two rows the recap arm shows in place of the
 *  "Buyer down payment" row (F2a Rail 3). */
export const RECAP_GLANCE_PURCHASE_PRICE = { label: "Purchase price", value: "Equals the amount financed" };
export const RECAP_GLANCE_PAID_AT_CLOSING = {
  label: "Paid at closing",
  value: "A note to your existing company, not cash",
};

/** Step 7's pill on the recap arm (F1/F2a). */
export const RECAP_PLAN_STEP_LABEL = "Plan";

// ── F2b — Step 2 · Business ────────────────────────────────────────────────

export const RECAP_BUSINESS_HINT =
  "Enter the company that owns and operates the business today. In an owner recapitalization it is the seller and Note Holder.";
export const RECAP_BUSINESS_NAME_HELPER = "Your existing company's legal name.";
export const RECAP_YEARS_OWNED_LABEL =
  "How many whole years have the current owners owned and operated the business?";
export const RECAP_YEARS_OWNED_HELPER =
  "Count whole years since the current owners took over. The program requires at least three.";
export const RECAP_YEARS_OWNED_UNDER_MINIMUM_NOTE =
  "The Owner Recapitalization Loan requires at least three whole years under the current owners. You can still finish, and our team will talk with you about timing.";
export const RECAP_YEARS_OWNED_REQUIRED_ERROR =
  "Enter the number of whole years the current owners have owned and operated the business.";
export const RECAP_YEARS_OWNED_WHOLE_NUMBER_ERROR = "Enter a whole number, such as 3.";

// ── F2c — Step 3 · Fit check (display text only; keys, polarity, triggers and
//    engine values are the sale arm's, unchanged) ──────────────────────────────

interface RecapKnockoutText {
  statement?: string;
  helperText?: string;
  followUpQuestion?: string;
  restrictExplanation?: string;
  unsureNote?: string;
}

const RECAP_KNOCKOUT_TEXT: Record<string, RecapKnockoutText> = {
  "seller-no-authority": {
    statement:
      "Do you and your co-owners hold clear, uncontested authority to have your existing company sell its business assets to a new company you form?",
    followUpQuestion:
      "To confirm: authority to sell the business assets isn't fully clear, or someone is contesting it — is that right?",
    restrictExplanation:
      "Everyone whose consent is needed must sign off on the asset sale, so if that's in question it's worth sorting out first — often it's a co-owner's or member's approval. This isn't a 'no'; let's talk about what it would take to get you there.",
    unsureNote:
      "If you're not certain — for example, your operating agreement requires member or board approval — mark it and we'll help you work out where things stand.",
  },
  "seller-surviving-debt": {
    statement:
      "If you move forward with Phoenix, will Phoenix's note be the only financing used to fund your new company's purchase?",
    helperText:
      "This is about financing brought in to fund the asset purchase — for example, a hard money, personal, or SBA loan. It does not include the business's everyday operating debt or an existing business loan; existing loans are covered separately in the application.",
    followUpQuestion:
      "To confirm: financing other than Phoenix's note — like a hard money, personal, or SBA loan — would be used to help fund the purchase — is that right?",
    restrictExplanation:
      "Phoenix is built to be the single financing behind the purchase, so another loan used to fund it changes the structure. It's not always a dealbreaker — let's talk through your situation. (This is only about financing used to fund the purchase, not everyday operating debt or an existing business loan.)",
    unsureNote:
      "This is about money brought in to fund the purchase itself, not normal operating debt or an existing business loan. If you're not sure, flag it and we'll walk through it.",
  },
  "seller-bankruptcy": {
    restrictExplanation:
      "While a business is in an active bankruptcy, we're not able to administer a recapitalization through Phoenix — that's a program-wide requirement. Once the proceeding is resolved, we'd be glad to revisit it with you.",
  },
  "seller-nontransferable-license": {
    statement:
      "Can every license, permit, and certification the business needs move to your new company, by transfer or reissue?",
    followUpQuestion:
      "To confirm: one or more required licenses or certifications may not move to your new company — is that right?",
    restrictExplanation:
      "If a license the business needs can't move to the new company, the new company may not be able to keep operating, so we'll want to look at it closely. A license held personally by an owner who stays is often not a problem. It doesn't necessarily stop the recapitalization — let's talk it through.",
    unsureNote:
      "If you're not sure whether a license would move to a new company, mark it — we can look into it together, and it won't block your result.",
  },
  "seller-tax-lien": {
    restrictExplanation:
      "In an owner recapitalization your existing company receives a note, not cash, so there are no sale proceeds at closing to pay a lien from — it needs to be cleared another way. If one genuinely can't be resolved by closing, we'll want to understand it — reach out and we'll work through the options with you.",
    unsureNote:
      "If you're unsure whether a lien could be cleared before closing, flag it and we'll help you sort it out.",
  },
  "seller-investigation": {
    restrictExplanation:
      "An open regulatory matter is something we need to understand before administering a recapitalization, since it can affect the business's operations and value. Depending on what it is, there may still be a path — let's talk about it rather than assume the worst.",
  },
  "seller-franchisor-denied": {
    followUpQuestion:
      "Has the franchisor consented to the franchise moving to your new company, or is consent not yet denied in writing?",
    restrictExplanation:
      "If the franchisor won't consent to the franchise moving to your new company, the new company can't operate under it, so we'd need to understand where that stands before going further. Let's talk about it. (If the business isn't a franchise, choose “No” on the first question above.)",
    unsureNote:
      "If you're not sure where the franchisor stands on the franchise moving to your new company, that's okay — we can work through it with you, and it won't block your result.",
  },
};

/** The nine seller knockouts with the recap arm's display text. Same keys, same
 *  order, same follow-up logic and engine values as {@link SELLER_KNOCKOUTS}
 *  (Q18); `seller-excluded-industry` and `seller-not-going-concern` are
 *  unchanged on the recap arm. */
export const SELLER_RECAP_KNOCKOUTS: KnockoutQuestionDef[] = SELLER_KNOCKOUTS.map((q) => {
  const t = RECAP_KNOCKOUT_TEXT[q.key];
  if (!t) return q;
  return {
    ...q,
    statement: t.statement ?? q.statement,
    helperText: t.helperText ?? q.helperText,
    restrictExplanation: t.restrictExplanation ?? q.restrictExplanation,
    unsureNote: t.unsureNote ?? q.unsureNote,
    followUp:
      q.followUp && t.followUpQuestion
        ? { ...q.followUp, followUpQuestion: t.followUpQuestion }
        : q.followUp,
  };
});

// ── F2d — Step 4 · Numbers ─────────────────────────────────────────────────

export const RECAP_NUMBERS_NOTE =
  "In an owner recapitalization the purchase price is the amount financed, so there is no asking price or down payment to enter.";

// ── F2e — Step 5 · Owner reliance ──────────────────────────────────────────

export const RECAP_OWNER_RELIANCE_HINT =
  "You are staying in the business, so this measures how much it depends on any one person.";

// ── F2g — Step 7 · Plan ────────────────────────────────────────────────────

export const RECAP_PLAN_TITLE = "What happens after closing?";
export const RECAP_MANAGEMENT_QUESTION = "Who will run the business day to day after closing?";
/** Values mirror PrequalSellerRecap.Management* (stable tokens). */
export const RECAP_MANAGEMENT_OPTIONS: ChoiceOption[] = [
  { value: "current-roles", label: "The current owners, in their current roles" },
  { value: "roles-changing", label: "The current owners, with some roles changing" },
  { value: "designated-operator", label: "A designated operator, overseen by the owners" },
];
export const RECAP_GOAL_QUESTION = "What do the owners want the recapitalization to achieve?";
export const RECAP_GOAL_HELPER = "A sentence or two is enough.";

// ── F2h — Step 8 · Finalize ────────────────────────────────────────────────

export const RECAP_FINALIZE_NOTICE_HEADING = "Use your existing company's email";
export const RECAP_FINALIZE_NOTICE_BODY =
  "This prequalification is recorded for your existing company, the seller and Note Holder. Enter that company's email. Your new company will need its own, different email when its account is opened.";
export const RECAP_FINALIZE_NAME_HELPER = "The owner completing this for your existing company.";
export const RECAP_FINALIZE_EMAIL_HELPER =
  "Your existing company's email — where your results and documents go.";

// ── F3a / F3b / F3d — result screens ───────────────────────────────────────

export const RECAP_RESULT_OVERLINE = "Business prequalified · Owner recapitalization";
export const RECAP_RESULT_SUB_LABEL = "Also the purchase price";
export const RECAP_RESULT_SIZING_RULE =
  "Financing is generally up to five times free cash flow on a 20-year schedule, and less on a 10-year schedule: the lower of 5× free cash flow and what the business can carry, with a margin, on the schedule your new company elects. In an owner recapitalization the purchase price equals the amount financed.";
export const RECAP_RESULT_NOTE_NOT_CASH = "Your existing company would receive a note for this amount, not cash.";

export const RECAP_LETTER_CARD_BLURB =
  "Confirms the business is screened for an owner recapitalization — share it with your business broker.";
export const RECAP_DEAL_SHEET_CARD_BLURB =
  "The amount financed on the 20- and 10-year schedules, and the combined costs both your companies bear.";

/** F3d — the noun the completed-application gate names on the recap arm. */
export const RECAP_COMPLETED_NOUN = "owner recapitalization";
